import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Eye, 
  Download,
  Calendar, 
  User, 
  Building, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowLeft,
  Activity,
  DollarSign,
  Shield
} from 'lucide-react';

// Mock contract data
const contracts = [
  {
    id: 'contract-001',
    contract_number: 'CT-2025-001',
    title: '鋁合金材料供應合約',
    customer_id: 'cust-001',
    customer_name: '台灣電子科技股份有限公司',
    contract_type: 'sales',
    status: 'active',
    value: 15000000,
    currency: 'TWD',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
    signed_date: '2024-12-15',
    payment_terms: '30天',
    delivery_terms: 'FOB 台北港',
    created_by: '業務經理_張三',
    created_date: '2024-12-10',
    contract_items: [
      {
        item_id: 'item-001',
        product_name: '鋁合金材料 A-grade',
        quantity: 50000,
        unit: '公斤',
        unit_price: 50,
        total_amount: 2500000
      },
      {
        item_id: 'item-002',
        product_name: '鋁合金材料 B-grade',
        quantity: 30000,
        unit: '公斤',
        unit_price: 45,
        total_amount: 1350000
      }
    ],
    terms_conditions: [
      '品質標準須符合 ISO 9001 認證',
      '交貨期限不得超過合約規定日期',
      '付款條件為收到發票後30天內付清',
      '如有品質問題可無條件退換貨'
    ],
    milestones: [
      {
        milestone_id: 'ms-001',
        description: '合約簽署',
        due_date: '2024-12-15',
        status: 'completed',
        completion_date: '2024-12-15'
      },
      {
        milestone_id: 'ms-002',
        description: '首批交貨',
        due_date: '2025-01-15',
        status: 'pending',
        completion_date: null
      }
    ]
  },
  {
    id: 'contract-002',
    contract_number: 'CT-2025-002',
    title: '精密零件製造服務合約',
    customer_id: 'cust-002',
    customer_name: '創新科技股份有限公司',
    contract_type: 'service',
    status: 'draft',
    value: 8500000,
    currency: 'TWD',
    start_date: '2025-02-01',
    end_date: '2025-07-31',
    signed_date: null,
    payment_terms: '45天',
    delivery_terms: 'EXW 工廠交貨',
    created_by: '業務經理_李四',
    created_date: '2025-01-10',
    contract_items: [
      {
        item_id: 'item-003',
        product_name: '精密零件 Type-A',
        quantity: 10000,
        unit: '件',
        unit_price: 850,
        total_amount: 8500000
      }
    ],
    terms_conditions: [
      '製造標準須符合客戶技術規格',
      '每批次須提供品質檢驗報告',
      '交貨前須經客戶驗收確認',
      '保固期為交貨後12個月'
    ],
    milestones: [
      {
        milestone_id: 'ms-003',
        description: '技術規格確認',
        due_date: '2025-01-20',
        status: 'pending',
        completion_date: null
      },
      {
        milestone_id: 'ms-004',
        description: '樣品製作完成',
        due_date: '2025-01-30',
        status: 'pending',
        completion_date: null
      }
    ]
  }
];

const ContractManagement = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const formatCurrency = (amount, currency = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-yellow-100 text-yellow-800',
      'active': 'bg-green-100 text-green-800',
      'completed': 'bg-blue-100 text-blue-800',
      'cancelled': 'bg-red-100 text-red-800',
      'expired': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'draft': '草稿',
      'active': '生效中',
      'completed': '已完成',
      'cancelled': '已取消',
      'expired': '已過期'
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'sales': '銷售合約',
      'service': '服務合約',
      'maintenance': '維護合約',
      'licensing': '授權合約'
    };
    return labels[type] || type;
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      if (statusFilter !== 'all' && contract.status !== statusFilter) {
        return false;
      }
      if (typeFilter !== 'all' && contract.contract_type !== typeFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          contract.contract_number.toLowerCase().includes(searchLower) ||
          contract.title.toLowerCase().includes(searchLower) ||
          contract.customer_name.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, typeFilter, searchTerm]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-purple-600">階段C: 商務談判與銷售訂單確認 - 合約管理</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalContracts = contracts.length;
    const activeContracts = contracts.filter(c => c.status === 'active').length;
    const totalValue = contracts.reduce((sum, c) => sum + c.value, 0);
    const draftContracts = contracts.filter(c => c.status === 'draft').length;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">合約管理儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增合約</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">合約總數</p>
                <p className="text-2xl font-bold text-gray-800">{totalContracts}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">生效合約</p>
                <p className="text-2xl font-bold text-gray-800">{activeContracts}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">合約總值</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalValue)}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">草稿合約</p>
                <p className="text-2xl font-bold text-gray-800">{draftContracts}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 合約列表 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">最新合約</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {contracts.map((contract) => (
                <div
                  key={contract.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedContract(contract);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{contract.title}</h4>
                      <p className="text-sm text-gray-600">{contract.contract_number} - {contract.customer_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">{formatCurrency(contract.value)}</p>
                      <p className="text-sm text-gray-600">{contract.start_date} ~ {contract.end_date}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                      {getStatusLabel(contract.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">合約列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增合約</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋合約..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="draft">草稿</option>
            <option value="active">生效中</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有類型</option>
            <option value="sales">銷售合約</option>
            <option value="service">服務合約</option>
            <option value="maintenance">維護合約</option>
            <option value="licensing">授權合約</option>
          </select>
        </div>
      </div>

      {/* 合約表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                合約資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                合約期間
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                合約價值
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredContracts.map((contract) => (
              <tr key={contract.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{contract.title}</div>
                      <div className="text-sm text-gray-500">{contract.contract_number}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{contract.customer_name}</div>
                  <div className="text-sm text-gray-500">{getTypeLabel(contract.contract_type)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{contract.start_date}</div>
                  <div className="text-sm text-gray-500">至 {contract.end_date}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(contract.value)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                    {getStatusLabel(contract.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedContract(contract);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-green-600 hover:text-green-900 mr-3">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="text-purple-600 hover:text-purple-900">
                    <Download className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedContract) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">合約詳細資訊</h2>
          <div className="flex items-center space-x-4">
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>下載合約</span>
            </button>
            <button
              onClick={() => setActiveView('list')}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回列表</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">合約編號:</span>
                <span className="font-medium">{selectedContract.contract_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">合約標題:</span>
                <span className="font-medium">{selectedContract.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">合約類型:</span>
                <span className="font-medium">{getTypeLabel(selectedContract.contract_type)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">合約狀態:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedContract.status)}`}>
                  {getStatusLabel(selectedContract.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">合約價值:</span>
                <span className="font-medium">{formatCurrency(selectedContract.value)}</span>
              </div>
            </div>
          </div>

          {/* 客戶資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">客戶資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">客戶名稱:</span>
                <span className="font-medium">{selectedContract.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">合約期間:</span>
                <span className="font-medium">{selectedContract.start_date} ~ {selectedContract.end_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">簽署日期:</span>
                <span className="font-medium">{selectedContract.signed_date || '尚未簽署'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款條件:</span>
                <span className="font-medium">{selectedContract.payment_terms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">交貨條件:</span>
                <span className="font-medium">{selectedContract.delivery_terms}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 合約項目 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">合約項目</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單價
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    總金額
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedContract.contract_items.map((item) => (
                  <tr key={item.item_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.product_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.unit_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(item.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 條款條件 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">條款與條件</h3>
          <ul className="space-y-2">
            {selectedContract.terms_conditions.map((term, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <span className="text-gray-700">{term}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 里程碑 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">執行里程碑</h3>
          <div className="space-y-4">
            {selectedContract.milestones.map((milestone) => (
              <div key={milestone.milestone_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    milestone.status === 'completed' ? 'bg-green-100' :
                    milestone.status === 'pending' ? 'bg-yellow-100' : 'bg-gray-100'
                  }`}>
                    {milestone.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : milestone.status === 'pending' ? (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">{milestone.description}</h4>
                    <p className="text-sm text-gray-600">預定日期: {milestone.due_date}</p>
                  </div>
                </div>
                <div className="text-right">
                  {milestone.completion_date && (
                    <p className="text-sm text-green-600">完成日期: {milestone.completion_date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增合約</h2>
              <p className="text-gray-600">合約新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default ContractManagement; 